import { useState, useEffect, useCallback, useMemo } from 'react';
import { safeGet, safeSet } from '../../utils/safeStorage';
import {
  RISK_UNIVERSE,
  computeResidualScore,
  bandFor,
  FRAUD_TRIGGER_IDS,
  PCC_MANDATORY_IDS,
  buildDefaultApplicability,
  emptyEvaluation,
  OPERATING_HEAD_IDS,
} from '../../data/riskUniverse';
import { STORAGE_KEYS } from './storageKeys';

// ─────────────────────────────────────────────────────────────────────────
// Risk module state model — corrected per HMSI governance framework
//
// MASTER FRAMEWORK (Admin, read-only for everyone else):
//   • RISK_UNIVERSE  — immutable 45-row HMSI master (code constant)
//   • applicability  — { [riskId]: ohId[] }  Admin maintains which OHs each
//                      risk applies to. Seeded from Business Units column.
//
// OPERATIONAL EVALUATION (Operating Head, one record per OH × risk):
//   • evaluations    — { [riskId]: { [ohId]: { concretePotentialRisk,
//                      currentSituation, preventiveMeasures, recoveryMeasures,
//                      frequency, impact, controlMeasure, confirmed } } }
//
// The legacy `riskUniverse` selector remains so downstream code (Fraud
// triggers, Dashboard, Linkage page) keeps working — but its F/I/C/confirmed
// columns are now derived as the WORST-CASE across all OH evaluations of
// that master risk. The selector mirrors the old shape exactly.
// ─────────────────────────────────────────────────────────────────────────

const initialApplicability = () =>
  safeGet(STORAGE_KEYS.riskApplicability, buildDefaultApplicability());

const initialEvaluations = () =>
  safeGet(STORAGE_KEYS.riskEvaluations, {});

// Default TRUE so the demo flow works immediately. Operating Head can Republish at will.
const initialPublished = () =>
  safeGet(STORAGE_KEYS.riskPublished, {
    published: true,
    version: 'v3.2',
    publishedAt: '2025-07-22T10:14:00Z',
    publishedBy: 'Priya Verma',
  });

export function useRiskState({ user, addNotification }) {
  const [applicability, setApplicability] = useState(initialApplicability);
  const [evaluations, setEvaluations] = useState(initialEvaluations);
  const [riskPublished, setRiskPublished] = useState(initialPublished);

  useEffect(() => { safeSet(STORAGE_KEYS.riskApplicability, applicability); }, [applicability]);
  useEffect(() => { safeSet(STORAGE_KEYS.riskEvaluations, evaluations); }, [evaluations]);
  useEffect(() => { safeSet(STORAGE_KEYS.riskPublished, riskPublished); }, [riskPublished]);

  // ── Admin actions: applicability matrix ──────────────────────────────
  const setRiskApplicability = useCallback((riskId, ohIds) => {
    setApplicability((m) => ({ ...m, [riskId]: [...new Set(ohIds)] }));
  }, []);

  const toggleApplicability = useCallback((riskId, ohId) => {
    setApplicability((m) => {
      const cur = m[riskId] || [];
      const next = cur.includes(ohId) ? cur.filter((x) => x !== ohId) : [...cur, ohId];
      return { ...m, [riskId]: next };
    });
  }, []);

  // ── OH actions: operational evaluation ───────────────────────────────
  const setEvaluation = useCallback((riskId, ohId, patch) => {
    setEvaluations((m) => {
      const master = RISK_UNIVERSE.find((r) => r.id === riskId);
      const prev = m[riskId]?.[ohId] || emptyEvaluation(master);
      return { ...m, [riskId]: { ...(m[riskId] || {}), [ohId]: { ...prev, ...patch } } };
    });
  }, []);

  // Aggregated worst-case-across-all-OHs view of each master risk — keeps the
  // legacy `riskUniverse` shape that the rest of the app (Fraud trigger,
  // Dashboard, Linkage page) reads.
  const riskUniverse = useMemo(() => RISK_UNIVERSE.map((master) => {
    const perOh = evaluations[master.id] || {};
    const evalRows = Object.values(perOh);
    // Worst-case = highest residual score across all OH evaluations
    let worst = null;
    let worstResidual = -1;
    evalRows.forEach((ev) => {
      const { residual } = computeResidualScore(ev);
      if (residual > worstResidual) { worstResidual = residual; worst = ev; }
    });
    const allConfirmed = evalRows.length > 0 && evalRows.every((ev) => ev.confirmed);
    return {
      ...master,
      // Worst-case F/I/C bubble up so existing RAG/band logic keeps working
      frequency: worst?.frequency ?? master.frequency,
      impact: worst?.impact ?? master.impact,
      controlMeasure: worst?.controlMeasure ?? master.controlMeasure,
      concretePotentialRisk: worst?.concretePotentialRisk || '',
      currentSituation: worst?.currentSituation || '',
      preventiveMeasures: worst?.preventiveMeasures || '',
      recoveryMeasures: worst?.recoveryMeasures || '',
      // Risk is "confirmed" when EVERY assigned OH has confirmed their eval
      confirmed: allConfirmed,
      // Bookkeeping
      ohEvaluationCount: evalRows.length,
      assignedOhs: applicability[master.id] || [],
      ownerRole: 'Operating Head',
    };
  }), [evaluations, applicability]);

  // Selectors per role / OH
  const myAssignedRisks = useCallback((u) => {
    if (!u) return [];
    if (u.role === 'operating_head' && u.operatingHead) {
      return riskUniverse.filter((r) => (r.assignedOhs || []).includes(u.operatingHead));
    }
    if (u.role === 'admin') return riskUniverse;
    return riskUniverse; // DH/Div Head/Legal: read-only browse of all
  }, [riskUniverse]);

  // Look up one OH's evaluation for a master risk (auto-creates empty when absent)
  const getEvaluation = useCallback((riskId, ohId) => {
    const master = RISK_UNIVERSE.find((r) => r.id === riskId);
    return evaluations[riskId]?.[ohId] || emptyEvaluation(master);
  }, [evaluations]);

  // All OH evaluations for a risk (for Admin's stacked view)
  const evaluationsForRisk = useCallback((riskId) => {
    const out = [];
    OPERATING_HEAD_IDS.forEach((ohId) => {
      if ((applicability[riskId] || []).includes(ohId)) {
        out.push({ ohId, evaluation: getEvaluation(riskId, ohId) });
      }
    });
    return out;
  }, [applicability, getEvaluation]);

  // ── Bulk admin actions ────────────────────────────────────────────────
  const confirmAllRisks = useCallback(() => {
    setEvaluations((cur) => {
      const next = { ...cur };
      RISK_UNIVERSE.forEach((master) => {
        const assigned = applicability[master.id] || [];
        next[master.id] = { ...(next[master.id] || {}) };
        assigned.forEach((ohId) => {
          const ev = next[master.id][ohId] || emptyEvaluation(master);
          next[master.id][ohId] = { ...ev, confirmed: true };
        });
      });
      return next;
    });
  }, [applicability]);

  const unconfirmAllRisks = useCallback(() => {
    setEvaluations((cur) => {
      const next = { ...cur };
      Object.keys(next).forEach((rid) => {
        const inner = { ...next[rid] };
        Object.keys(inner).forEach((ohId) => { inner[ohId] = { ...inner[ohId], confirmed: false }; });
        next[rid] = inner;
      });
      return next;
    });
  }, []);

  const publishRiskAssessment = useCallback(() => {
    const unconfirmed = riskUniverse.filter((r) => !r.confirmed).length;
    if (unconfirmed > 0) {
      return { ok: false, error: `Cannot publish — ${unconfirmed} risk${unconfirmed === 1 ? '' : 's'} still pending confirmation across assigned Operating Heads.` };
    }
    const now = new Date();
    const nextVersion = `v3.${Math.floor(now.getTime() / 1000) % 1000}`;
    setRiskPublished({
      published: true,
      version: nextVersion,
      publishedAt: now.toISOString(),
      publishedBy: user?.name || 'Operating Head',
    });
    addNotification({
      type: 'approved',
      targetRole: 'department_head',
      title: 'Risk Universe published',
      body: `${user?.name || 'Operating Head'} published ${nextVersion}. Your PCC / Fraud / DLC tasks are now active.`,
      link: '/my-tasks',
    });
    return { ok: true, version: nextVersion };
  }, [user, addNotification, riskUniverse]);

  const unpublishRiskAssessment = useCallback(() => {
    setRiskPublished((rp) => ({ ...rp, published: false }));
  }, []);

  // Back-compat alias for any code that still calls `updateRisk` (e.g. an OH
  // patching F/I/C on the aggregated view). Maps to setEvaluation for the
  // current OH viewer when patch shape is risk fields, otherwise no-op.
  const updateRisk = useCallback((riskId, patch) => {
    if (!user || user.role !== 'operating_head' || !user.operatingHead) return;
    setEvaluation(riskId, user.operatingHead, patch);
  }, [user, setEvaluation]);

  // Derived: fraud trigger flag, triggering rows, PCC-mandatory rows
  const { fraudTriggered, triggeringRisks, pccMandatory } = useMemo(() => {
    const trig = riskUniverse
      .filter((r) => FRAUD_TRIGGER_IDS.includes(r.id))
      .map((r) => {
        const { inherent, residual } = computeResidualScore(r);
        return { ...r, inherent, residual, band: bandFor(residual) };
      });
    return {
      fraudTriggered: trig.some((r) => r.band === 'Medium' || r.band === 'High'),
      triggeringRisks: trig,
      pccMandatory: riskUniverse.filter((r) => PCC_MANDATORY_IDS.includes(r.id)),
    };
  }, [riskUniverse]);

  return {
    riskUniverse,
    applicability,
    evaluations,
    setRiskApplicability,
    toggleApplicability,
    setEvaluation,
    getEvaluation,
    evaluationsForRisk,
    myAssignedRisks,
    updateRisk,           // legacy shim
    confirmAllRisks,
    unconfirmAllRisks,
    riskPublished,
    publishRiskAssessment,
    unpublishRiskAssessment,
    fraudTriggered,
    triggeringRisks,
    pccMandatory,
  };
}
